<script context="module">
  import { Form } from "@ui/form";
  import { postJson } from "@ui/http";
  import { EmailField, TextField, TextAreaField, SelectField, PhoneField } from "@ui/form/fields";
  import DistrictAutoCompleteField from "./DistrictAutoCompleteField.svelte";
  import PositionField from "./PositionField.svelte";

  const codeToMessage = {
    invalid_email_format: "Invalid email format",
    invalid_phone: "Invalid phone number provided",
    invalid_email: "Invalid email address provided",
    missing_phone: "No phone number provided",
    missing_email: "No email address provided"
  };

  const NUMBER_OF_ACCOUNTS = [
    { text: "Just myself", value: "1" },
    { text: "My team", value: "30" },
    { text: "My department", value: "50" },
    { text: "My school", value: "100" },
    { text: "My district", value: "101" }
  ];
</script>

<script>
  export let user;

  let isFormDirty;
  $: if (isFormDirty) {
    try {
      const result = postJson("/app/users/log_client_event_json", {
        event: "started filling a demo form"
      });
    } catch {
      // there's nothing we can do ;(
    }
    try {
      postJson("/app/users/log_client_goal_json", { key: "billing/get-a-demo-form-started" });
    } catch {
      // there's nothing we can do ;(
    }
  }

  let data = {};
  $: if (user) {
    data.firstName = user.firstName;
    data.lastName = user.lastName;
    data.email = user.email;
  }

  let updateErrorMessage;
  let showErrorMessage = false;
  let postingTheData = false;

  async function submitForm(submitData) {
    const dataToSubmit = { ...submitData, phone: submitData.phone.replace(/-/g, "").replace(/\+/g, "") };
    if (!submitData.schoolName) {
      delete dataToSubmit.schoolName;
    }

    if (submitData.position === "Other") {
      dataToSubmit.position = submitData.otherPosition;
      delete dataToSubmit.otherPosition;
    }

    try {
      showErrorMessage = false;
      updateErrorMessage = null;
      postingTheData = true;

      // Send the requests to our servers
      postJson("/app/users/log_client_event_json", {
        event: "submitted a demo form"
      });
      await postJson("/app/marketing/submit_inquiry_form", dataToSubmit);

      // Do not wait for them to return, because it blocks the user for about 4 secs
      // So, we'll redirect to Calendly after 200 milliseconds
      setTimeout(() => {
        window.location.href = "/app/marketing/demo_signup_thank_you";
      }, 200);
    } catch (ex) {
      updateErrorMessage = codeToMessage[ex.code] || "Oops, something went wrong";
      showErrorMessage = true;
      setTimeout(() => {
        showErrorMessage = false;
      }, 2500);
    } finally {
      postingTheData = false;
    }
  }
</script>

<div class="relative w-full overflow-x-hidden sm-form">
  <div
    class:show-error={showErrorMessage}
    class="absolute z-10 flex items-center justify-center w-full py-3 text-white bg-red-600 error-message">
    {updateErrorMessage}
  </div>
  <div class="absolute inset-0 z-10 bg-gray-300 opacity-50" class:hidden={!postingTheData} />
  <div class="absolute inset-0 z-20 flex items-center justify-center bg-transparent" class:hidden={!postingTheData}>
    <div class="px-16 py-6 border rounded shadow-md bg-gray-50">Please wait...</div>
  </div>
  <Form bind:data submit={submitForm} on:cancel bind:isDirty={isFormDirty}>
    <div class="flex flex-col items-start justify-start w-full p-5 pb-0">
      <div class="mb-4 font-bold text-20">Contact the sales team</div>
      <div class="row">
        <div class="column">
          <PositionField key="position" name="I am a..." required autofocus placeholder="Choose an option" />
        </div>
        <div class="column">
          <SelectField
            key="numberOfAccounts"
            required
            name="Who will be using Smore?"
            placeholder="Please select"
            options={NUMBER_OF_ACCOUNTS} />
        </div>
      </div>
      <div class="row">
        <div class="relative column">
          <DistrictAutoCompleteField
            key="district"
            id="org-district-name"
            name="District Name"
            hint="(if it doesn't appear, enter name manually)"
            required
            placeholder="Search by district, city or zip code" />
        </div>
      </div>
      <div class="row">
        <div class="column">
          <TextField key="schoolName" name="School Name" placeholder="School name" />
        </div>
      </div>
      <div class="my-4 font-bold text-20">Tell us about yourself</div>
      <div class="row">
        <div class="column">
          <TextField key="firstName" name="First Name" placeholder="Your name" required />
        </div>
        <div class="column">
          <TextField key="lastName" name="Last Name" placeholder="Your last name" required />
        </div>
      </div>
      <div class="row">
        <div class="column">
          <EmailField key="email" name="Email" placeholder="Your email" required />
        </div>
        <div class="column">
          <PhoneField key="phone" name="Contact Phone" required placeholder="Your phone number" extension={true} />
        </div>
      </div>
      <div class="row">
        <div class="column">
          <TextAreaField
            required={true}
            name="How do you hope to achieve your goals with a Smore for Teams account?"
            key="learnInDemo"
            placeholder="I am hoping to streamline communication in our school/district, as well as create a culture of consistent communication." />
        </div>
      </div>
    </div>

    <div
      slot="footer"
      class="flex items-center justify-center w-full px-6 pb-5 mt-4 bg-white align-center"
      let:canSubmit
      let:onSubmit
      let:isSubmitting>
      <input
        type="submit"
        disabled={isSubmitting || !canSubmit}
        class="w-11/12 py-3 my-4 text-base font-medium text-center text-white transition-colors rounded shadow outline-none cursor-default sm:w-5/6 form-input sm-btn"
        on:click|stopPropagation|preventDefault={onSubmit}
        class:cursor-pointer={canSubmit}
        class:hover:bg-blue-500={canSubmit}
        class:sm-clickable={canSubmit}
        class:bg-blue-600={canSubmit}
        class:bg-gray-200={isSubmitting || !canSubmit}
        class:text-gray-400={isSubmitting || !canSubmit}
        class:shadow-none={isSubmitting || !canSubmit}
        value={isSubmitting ? "Scheduling a demo" : "Get a demo & quote"} />
    </div>
  </Form>
</div>

<style>
  .row {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: start;
    width: 100%;
    padding-right: 3px;
  }
  /* Medium (md) */
  @media (min-width: 768px) {
    .row {
      flex-direction: row;
    }
  }
  .row + .row {
    margin-top: 10px;
  }
  .column {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-items: start;
    width: 100%;
  }
  :global(.sm-form .column textarea),
  :global(.sm-form .column input[type="text"]) {
    width: 100%;
  }
  :global(.sm-form .column label) {
    font-size: 14px;
    font-weight: normal;
    line-height: 16px;
    margin-bottom: 5px;
  }
  .column + .column {
    margin-top: 1rem;
  }
  /* Medium (md) */
  @media (min-width: 768px) {
    .column + .column {
      margin-top: 0;
      margin-left: 1rem;
    }
  }
  .error-message {
    transition: all 0.5s ease-in-out;
    opacity: 0;
    transform: translateY(-100%);
  }
  .error-message.show-error {
    opacity: 1;
    transform: translateY(0);
  }

  :global(.algolia-autocomplete .aa-dropdown-menu) {
    border-radius: 4px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  :global(.algolia-autocomplete .aa-dropdown-menu .aa-suggestion) {
    padding: 10px;
  }

  :global(.algolia-autocomplete .aa-dropdown-menu .aa-suggestion h4),
  :global(.algolia-autocomplete .aa-dropdown-menu .aa-suggestion h5) {
    display: block;
  }
  :global(.algolia-autocomplete .aa-dropdown-menu .aa-suggestion h5 span) {
    color: #ad2825;
  }

  :global(.algolia-autocomplete .aa-dropdown-menu .aa-dataset-1) {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
</style>
